/**
 * localStorage persistence for a two-level blob — an outer scope key (schoolId /
 * classId / userId) mapping to a record of items.
 *
 * Callers keep per-scope caches that must survive whatever a previous version,
 * another tab, or a corrupted write left behind, so every read is structurally
 * validated and every failure degrades to an empty blob.
 */

import { localStorage } from "./localStorage";

/**
 * Narrow an unknown value to a plain object — excludes null and arrays, both of
 * which are `typeof "object"`. Used when validating parsed JSON.
 */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Validate the outer structure of parsed localStorage data.
 * Returns only scope entries that are valid objects.
 */
export function validateScopedStorageStructure(parsed: unknown): Record<string, Record<string, unknown>> {
  if (!isPlainObject(parsed)) return {};
  const result: Record<string, Record<string, unknown>> = {};
  for (const [key, value] of Object.entries(parsed)) {
    if (isPlainObject(value)) {
      result[key] = value;
    }
  }
  return result;
}

/**
 * Read and structurally validate a scoped blob.
 *
 * Malformed JSON and unavailable storage (private browsing, quota) both yield
 * an empty blob rather than throwing: this data is a convenience cache, and
 * losing it must never break the caller.
 */
export function readScopedStorage(storageKey: string): Record<string, Record<string, unknown>> {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    return validateScopedStorageStructure(parsed);
  } catch (err) {
    if (err instanceof DOMException || err instanceof SyntaxError) return {};
    throw err;
  }
}

/**
 * Write a scoped blob, dropping scopes that have no items and removing the key
 * entirely when nothing is left. A write that fails because storage is
 * unavailable is swallowed, for the same reason reads are.
 */
export function writeScopedStorage(storageKey: string, all: Record<string, Record<string, unknown>>): void {
  try {
    const populated = Object.fromEntries(Object.entries(all).filter(([, items]) => Object.keys(items).length > 0));
    if (Object.keys(populated).length === 0) {
      localStorage.removeItem(storageKey);
    } else {
      localStorage.setItem(storageKey, JSON.stringify(populated));
    }
  } catch (err) {
    if (err instanceof DOMException) return;
    throw err;
  }
}
